import React, { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { groupPatientObservations } from '@eluve/fhir-observations';

import { GoalDetailDrawer } from './components/GoalDetailDrawer';
import { GoalsCard } from './components/GoalsCard';
import {
  useFhirPatientId,
  usePatientGoals,
  useTrackedObservations,
} from './data/goals';
import { buildGoalList, findGoalByToken } from './goal-list';

/**
 * The Goals vertical slice for the new patient profile Overview tab:
 * resolves the FHIR Patient from the route's (Hasura) patient id, loads the
 * goal connection plus the observation window tracked series resolve from,
 * and owns the card + detail drawer. The open drawer lives in the URL
 * (`?goal=<id>`) so detail views stay deep-linkable — routing, not
 * component state, is the source of truth.
 */
const DRAWER_PARAM = 'goal';

export const PatientGoalsSection: React.FC = () => {
  const { patientId } = useParams() as { patientId: string };
  const [searchParams, setSearchParams] = useSearchParams();

  const fhirPatient = useFhirPatientId(patientId);
  const goals = usePatientGoals(fhirPatient.data ?? null);
  const observations = useTrackedObservations(fhirPatient.data ?? null);

  const list = useMemo(() => buildGoalList(goals.data ?? []), [goals.data]);
  // A failed/empty observation read degrades to rows without trends — the
  // goals themselves stay up.
  const grouped = useMemo(
    () =>
      observations.data ? groupPatientObservations(observations.data) : null,
    [observations.data],
  );

  const openToken = searchParams.get(DRAWER_PARAM);
  const openGoal = findGoalByToken(list, openToken);

  const openDrawer = useCallback(
    (token: string) => {
      setSearchParams(
        (params) => {
          params.set(DRAWER_PARAM, token);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const closeDrawer = useCallback(() => {
    setSearchParams(
      (params) => {
        params.delete(DRAWER_PARAM);
        return params;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  return (
    <>
      <GoalsCard
        list={list}
        grouped={grouped}
        loading={fhirPatient.loading || goals.loading}
        failed={fhirPatient.failed || goals.failed}
        paging={goals}
        onOpenGoal={openDrawer}
      />
      <GoalDetailDrawer
        goal={openGoal}
        grouped={grouped}
        open={Boolean(openToken && openGoal)}
        onClose={closeDrawer}
        onReviewed={goals.refetch}
      />
    </>
  );
};
